<template>
  <div v-if="!taking && !editing" class="lesson-widget-summary">
    <p>{{ summary }}</p>
  </div>

  <div class="widget-card--wrap" v-else>
    <editor v-if="!taking" :item="item" :lessonId="lesson.id" />
    <v-card>
      <v-card-text>
        <p v-if="!taking">
          Schedule will appear when testing/taking this lesson.
        </p>
        <div v-else>
          <h5 v-if="completed && val.success_text" class="mb-2 mt-2">
            {{ val.success_text }}
          </h5>
          <v-btn
            v-if="widgetLoaded"
            :disabled="completed"
            @click="openModal"
            :text="!taking"
            color="primary"
          >
            {{ val.toggle_text }}
          </v-btn>
          <v-btn
            v-if="skipText"
            text
            :disabled="completed"
            @click="completeWidget"
            color="primary"
            class="skip-cal"
          >
            {{ skipText }}
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import GenericWidget from '../genericWidget/GenericWidget';
import EngagementWidget from '../engagementWidget/EngagementWidget';
import debounce from 'lodash/debounce';
import { mapGetters, mapMutations } from 'vuex';
import embedCalJs from './embed';
import { waitUntil } from '@/lib/misc';

export default {
  name: 'CalWidget',
  mixins: [GenericWidget, EngagementWidget],
  data() {
    return {
      //true when Cal.loaded
      widgetLoaded: false,
      //used my Cal('modal')
      uid: String(Date.now()),
      processedBooking: false,
    };
  },
  watch: {
    // if eventUrl changes we want to update configuration
    eventUrl() {
      this.loadWidget();
    },
    // monitor when this lesson widget is active
    active: {
      immediate: true,
      handler(isActive) {
        if (isActive && this.showModal) {
          this.openModal();
        }
      },
    },
  },
  computed: {
    ...mapGetters(['profile']),
    summary() {
      return this.val?.toggle_text;
    },
    showModal() {
      return this.taking && this.active && !this.completed;
    },
    skipText() {
      return this.val?.skip_text;
    },
    eventUrl() {
      return this.valCompiled('url');
    },
    parsedEventUrl() {
      const eventUrl = this.eventUrl;
      if (!eventUrl || !eventUrl.startsWith('https')) {
        return {};
      }
      return new URL(eventUrl);
    },
    completed() {
      return this.item?.engagementState?.completed;
    },
  },
  methods: {
    ...mapMutations(['updateProfileField']),

    loadWidget: debounce(async function (show) {
      if (!show && window.Cal) {
        if (Cal.instance?.actionManager) {
          Cal.instance.actionManager.fire('__closeIframe');
        }
      }

      const parsedEventUrl = this.parsedEventUrl;
      if (!parsedEventUrl) {
        return;
      }

      embedCalJs({
        origin: parsedEventUrl.origin,
      });
      this.listenForSuccessfullBooking();
      await waitUntil(() => window.Cal.loaded, 100);
      if (show) {
        this.openModal();
      }
      this.widgetLoaded = true;
    }, 300),

    listenForSuccessfullBooking() {
      Cal('on', {
        action: 'bookingSuccessful',
        callback: (e) => {
          console.log({ bookingSuccessful: e });
          if (this.processedBooking) return;
          this.processedBooking = true;

          const booking = e.detail?.data?.booking;
          if (!booking) return;

          let startTime = (this.localAnswer = booking.startTime || '');
          this.updateProfileFieldForInstantRetrieval(startTime);
          this.trackScheduledTimeEvent(booking);
          this.saveAnswer();
          Cal.instance.actionManager.fire('__closeIframe');
          this.completeWidget();
        },
      });
    },

    updateProfileFieldForInstantRetrieval(value) {
      if (!this.portal) return;
      const field = this.item.values.contact_field;
      this.updateProfileField({ field, value });
    },

    trackScheduledTimeEvent(booking) {
      const url = this.parsedEventUrl;

      const properties = {
        cal_id: booking.id,
        cal_uid: booking.uid,
        cal_contact_field: this.item.values.contact_field,
        cal_start_time: booking.startTime,
        cal_end_time: booking.endTime,
        cal_location: booking.location,
        cal_title: booking.title,
        cal_host: url.host,
        cal_path: url.pathname,
      };

      console.log({ properties, booking });

      this.lesson.trackItemEvent(this.item, 'cal_scheduled_time', properties);
    },

    openModal() {
      if (!window.Cal) {
        this.checkWidgetLoaded();
        return;
      }

      const parsedEventUrl = this.parsedEventUrl;
      const prefill = this.constructPrefill();
      const payload = {
        calLink: parsedEventUrl.pathname,
        uid: this.uid,
        calOrigin: parsedEventUrl.origin,
        config: {
          ...prefill,
        },
      };
      Cal('modal', payload);
    },

    checkWidgetLoaded() {
      let attempt = 0;
      const LIMIT = 20;

      let checker = setInterval(() => {
        if (window.Cal) {
          this.openModal();
        }

        if (window.Cal || attempt > LIMIT) {
          clearInterval(checker);
        }
        attempt += 1;
      }, 100);
    },

    constructPrefill() {
      const prefill = this.getPrefillFields();

      const getValue = (key) => {
        if (key === 'name') {
          return `${getValue('first_name')} ${getValue('last_name')}`;
        }

        let val = this.profile[key] || '';
        if (key === 'mobile' && val) {
          val += '';
          if (!val.startsWith('+')) {
            val = '+1' + val.replaceAll(/\D/g, '').slice(-10);
          }
        }

        return val;
      };

      prefill.conveyour_pid = getValue('pid');
      prefill.name = getValue('name');
      prefill.email = getValue('email');
      prefill.mobile = getValue('mobile');

      return prefill;
    },

    getPrefillFields() {
      const prefillFields = this.valCompiled('prefill_fields') || [];
      const config = {};
      prefillFields.forEach((field) => {
        const key = field.key;
        const value = field.value;
        if (key && value) {
          config[key] = value;
        }
      });
      return config;
    },

    completeWidget() {
      this.complete();
      this.$emit('ended', this.item);
    },
  },
  mounted() {
    if (this.taking) {
      this.loadWidget();
    }
  },
};
</script>

<style type="text/css">
.button-widget {
  text-align: center;
}
.button-widget .editor {
  text-align: left;
}

.v-btn.skip-cal {
  min-width: 0 !important;
  margin-left: 12px;
}
</style>
